import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { captureException } from '@/utils/sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Uncaught error:', error, errorInfo);
    
    // Log error to Sentry
    if (process.env.NODE_ENV === 'production') {
      captureException(error, {
        componentStack: errorInfo.componentStack,
        url: window.location.href,
        path: window.location.pathname
      });
    }
    
    // Call onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  }

  public render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Default fallback UI
      return (
        <div className="min-h-[50vh] flex flex-col items-center justify-center p-6 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Oups, quelque chose s'est mal passé
          </h2>
          
          <p className="text-gray-600 mb-6 max-w-md">
            Une erreur inattendue est survenue. Nos équipes ont été notifiées et travaillent à résoudre le problème.
          </p>
          
          {this.state.error && (
            <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg text-left max-w-md">
              <p className="text-sm font-mono text-gray-700 break-words">
                {this.state.error.toString()}
              </p>
            </div>
          )}
          
          <div className="flex gap-4">
            <Button
              variant="outline"
              onClick={() => window.location.href = '/'}
            >
              Retour à l'accueil
            </Button>
            
            <Button
              variant="primary"
              onClick={this.handleReset}
            >
              Réessayer
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;